/**
 * ExcludeToolbarStrip — the secondary controls, in one quiet strip.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Bulk scope and quick-start presets used to be two separate bars competing
 * with the rules themselves. They are housekeeping, so they get one muted
 * container above the list and stay visually below it in the hierarchy.
 */

import { FC, ReactNode } from 'react';
import type { FlowLayer } from '@trafficpulse/shared';

import ExcludeBulkToolbar from './ExcludeBulkToolbar';
import ExcludePresets from './ExcludePresets';
import type { ExcludePreset } from '../exclude-presets';

export interface ExcludeToolbarStripProps {
  groupCount: number;
  onSetAllLayers: (layers: ReadonlyArray<FlowLayer>) => void;
  onClearAll: () => void;
  onApplyPreset: (preset: ExcludePreset) => void;
  /** Library set controls (Save as set… / Load set) — composed upstream. */
  libraryActions?: ReactNode;
}

export const ExcludeToolbarStrip: FC<ExcludeToolbarStripProps> = ({
  groupCount,
  onSetAllLayers,
  onClearAll,
  onApplyPreset,
  libraryActions,
}) => (
  <div className="space-y-2 rounded-md border border-border/70 bg-muted/15 px-2.5 py-2">
    <ExcludeBulkToolbar
      groupCount={groupCount}
      onSetAllLayers={onSetAllLayers}
      onClearAll={onClearAll}
    />
    <span className="block h-px bg-border/50" aria-hidden />
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Quick start
      </span>
      <ExcludePresets layer="all layers" onApply={onApplyPreset} />
      {libraryActions ? (
        <span className="ml-auto flex items-center gap-2">{libraryActions}</span>
      ) : null}
    </div>
  </div>
);

export default ExcludeToolbarStrip;
